import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import LocalStorageHelper from '../../helpers/local_storage_helper';
import StartNewProposal from './start_new_proposal';
import QuickCheckPage from '../quickcheck/quickcheck_page';
import Constants from '../../helpers/constants';
import TabPanel from '../quickcheck/share/tab_panel';
import FunctionalityAccessHelper from '../../helpers/functionality_access_helper';
import ProductComparisonPage from './product_comparison_page';
import { Paper, Tab, Tabs } from '@mui/material';
import Actions from '../../actions/actions';
import AuthHelper from '../../helpers/auth_helper';
import StartNewCashProposal from './start_new_cash_proposal';
import ProductNotAvailable from '../shared/product_not_available';

const zipCodeTab = { id: 'proposal-calculator', label: 'Apply', child: <StartNewProposal />, priority: 2 };
const productComparisonTab = {
  id: 'product-comparison',
  label: 'Product Comparison',
  child: <ProductComparisonPage />,
  priority: 1,
};

const NewProposalPage = ({ isInstallerUser }) => {
  const user = useSelector((redux) => redux.user);
  const userFunctionalityAcls = useSelector((redux) => redux.userFunctionalityAcls);
  const isGuestUser = useSelector((redux) => redux.isGuestUser);
  const view = useSelector((redux) => redux.view);

  const [currentStep, setCurrentStep] = useState(0);
  const [visibleTabs, setVisibleTabs] = useState([]);

  const quickCheckTab = useMemo(
    () => ({
      id: 'prequalify',
      label: 'Prequalify',
      child: <QuickCheckPage {...{ isInstallerUser }} />,
      priority: 99,
    }),
    [isInstallerUser]
  );

  useEffect(() => {
    LocalStorageHelper.clearAllData();
    Actions.clearPolling();
  }, []);

  useEffect(() => {
    const quickCheckEnabled = FunctionalityAccessHelper.hasACL(
      userFunctionalityAcls,
      Constants.FUNCTIONALITY_ACL_FEATURE_CODE_QUICK_CHECK
    );
    const productComparisonEnabled = FunctionalityAccessHelper.hasACL(
      userFunctionalityAcls,
      Constants.FUNCTIONALITY_ACL_PRODUCT_COMPARISON
    );

    let tabList = [];
    if (isInstallerUser && (AuthHelper.hasProduct(user, 'SLOAN_') || AuthHelper.hasProduct(user, 'BATTERY_'))) {
      if (productComparisonEnabled) tabList.push(productComparisonTab);
      tabList.push(zipCodeTab);
    } else {
      if (isGuestUser || AuthHelper.hasProduct(user, 'SLOAN_') || AuthHelper.hasProduct(user, 'BATTERY_')) {
        tabList.push(quickCheckTab);
      }
    }

    if (
      isInstallerUser &&
      quickCheckEnabled &&
      (AuthHelper.hasProduct(user, 'SLOAN_') ||
        AuthHelper.hasProduct(user, 'HVAC_') ||
        AuthHelper.hasProduct(user, 'BATTERY_'))
    ) {
      tabList.push(quickCheckTab);
    }

    // if (AuthHelper.hasProduct(user, 'HVAC_')) {
    //   tabList.push({ id: 'hvac-proposal', label: 'Hvac', child: <StartNewHVACProposal />, priority: 5 });
    // }

    if (AuthHelper.hasProduct(user, 'CASH_')) {
      tabList.push({ id: 'cash-proposal', label: 'Cash', child: <StartNewCashProposal />, priority: 4 });
    }

    let initialViewIndex = tabList.reduce(
      (acc, cur, i) => (cur.priority > acc.best.priority ? { best: cur, index: i } : acc),
      { best: { priority: -1 }, index: 0 }
    ).index;

    if (view?.tab) {
      const navTab = tabList.findIndex((tab) => tab.id === view?.tab);
      if (navTab > -1) {
        initialViewIndex = navTab;
      }
    }

    setCurrentStep(initialViewIndex);
    setVisibleTabs(tabList);
  }, [view, isGuestUser, isInstallerUser, userFunctionalityAcls, quickCheckTab, user]);

  if (
    isInstallerUser &&
    !AuthHelper.getImpersonateCookie() &&
    FunctionalityAccessHelper.getUserSettingValueByName('is_training_required', user) === 'true' &&
    !user?.userAgreementComp
  ) {
    Actions.setRoute('/training-required');
    return <div></div>;
  }

  if (visibleTabs.length === 0 && !isGuestUser) {
    return (
      <ProductNotAvailable
        sorry={"Sorry, we don't have any available products for your company."}
        clickHereLabel={''}
        clickHereMsg={''}
        clickHerePrefix={`Please contact your company's admin for more information.`}
        clickHereUrl={''}
      />
    );
  }

  if (visibleTabs.length === 1) {
    return visibleTabs[0].child;
  }

  return (
    <>
      <Paper>
        <Tabs
          value={currentStep}
          onChange={(prev, next) => setCurrentStep(next)}
          indicatorColor="primary"
          textColor="primary"
          centered
        >
          {visibleTabs.map((vt, index) => (
            <Tab key={index} name={vt.id} id={vt.id} label={vt.label} />
          ))}
        </Tabs>
      </Paper>
      {visibleTabs.map((vt, index) => (
        <TabPanel value={currentStep} index={index} key={index} className={'new-proposal-tab-panel'}>
          {vt.child}
        </TabPanel>
      ))}
    </>
  );
};

NewProposalPage.propTypes = {
  isInstallerUser: PropTypes.bool.isRequired,
};

export default NewProposalPage;
